import {
  AccessLevel,
  Event,
  EventType,
  SelectRecipientGroupType,
  SelectRecipientType,
} from "@markit/common.types";
import { useSelector } from "react-redux";
import { getAccountSlice } from "../../../../../redux/slices/accountSlice";
import { useCallback, useMemo, useState } from "react";
import filter from "lodash.filter";
import FlatList from "flatlist-react/lib";
import SearchBoxContainer from "../../../../Containers/SearchBoxContainer";
import { getEventSlice } from "../../../../../redux/slices/eventSlice";
import { GenericActionItem } from "../../../../DisplayItem/GenericActionItem";
import { HorizontalDivider } from "../../../../Dividers/HorizontalDivider";
import { Icon } from "@iconify/react";
import { Colors } from "../../../../../utils/colors";
import { EmptyStateFlatlist } from "../../../../EmptyStates/EmptyStateFlatlist";
import {
  foundUserLinkForms,
  foundUserProducts,
  GetDateWithTime,
  isEventRelatedType,
  isExternalLinkNoData,
  isProductSpecificType,
} from "@markit/common.utils";
import EmptyStateButton from "../../../../Buttons/EmptyStateButton";
import EventItemImage from "../../../../DisplayItem/EventItem/EventItemImage";
import ListTabs from "../../../../DropdownsAndTabs/ListTabs";
import { getEventDisplayDate } from "../../../../../utils/GetLongDate";

type SelectRecipientsEventLibraryProps = {
  updateSelectType: (selectType: Partial<SelectRecipientGroupType>) => void;
  selectGroupType: SelectRecipientGroupType;
};

const SelectRecipientsEventLibrary = (
  props: SelectRecipientsEventLibraryProps
) => {
  const { updateSelectType, selectGroupType } = props;
  const { accountData } = useSelector(getAccountSlice);
  const { events } = useSelector(getEventSlice);
  const [searchTerm, setSearchTerm] = useState("");
  const [stateValue, setStateValue] = useState(0);

  const selectType = useMemo(() => selectGroupType.type, [selectGroupType]);

  const hostOnlyEvents = useMemo(
    () => events.filter((event) => event.createdBy === accountData.uid),
    [events, accountData.uid]
  );

  const eventsToShow = useMemo(() => {
    const products = foundUserProducts(
      hostOnlyEvents,
      accountData.uid,
      selectGroupType.eventType || EventType.MARKIT,
      stateValue === 0 ? "active" : "inactive"
    );
    if (
      selectGroupType.eventType &&
      isProductSpecificType(selectGroupType.eventType)
    ) {
      return products
        .filter((product) => product.accessLevel === AccessLevel.CHECKOUT)
        .reverse();
    }
    return products;
  }, [hostOnlyEvents, accountData.uid, selectGroupType.eventType, stateValue]);

  const formsToShow = useMemo(
    () =>
      foundUserLinkForms(
        hostOnlyEvents,
        accountData.uid,
        stateValue === 0 ? "active" : "inactive"
      ),
    [hostOnlyEvents, accountData.uid, stateValue]
  );

  const userItems = useMemo(
    () =>
      (selectType === SelectRecipientType.EVENTS
        ? stateValue === 0
          ? eventsToShow
          : eventsToShow.reverse()
        : stateValue === 0
        ? formsToShow
        : formsToShow.reverse()
      ).filter((item) => !isExternalLinkNoData(item.eventType)),
    [selectType, stateValue, eventsToShow, formsToShow]
  );

  const containsItem = useCallback((item: Event, query: string) => {
    return item.title.toLowerCase().includes(query.toLowerCase());
  }, []);

  const handleSearch = (text: string) => {
    setSearchTerm(text);
  };

  const itemsToShow: Event[] = useMemo(() => {
    let searchResults: any[] = userItems;
    if (searchTerm !== "") {
      searchResults = filter(userItems, (item: Event) => {
        return containsItem(item, searchTerm);
      });
    }
    return searchResults;
  }, [userItems, searchTerm, containsItem]);

  return (
    <>
      <div className="ColumnNormal" style={{ gap: 10, paddingBottom: 7 }}>
        <SearchBoxContainer
          value={searchTerm}
          placeholder={"Search..."}
          onChange={(e) => {
            handleSearch(e.target.value);
          }}
          containerStyles={{ marginTop: 0 }}
        />
        <ListTabs
          tabLabels={["Active", "Inactive"]}
          tabNumbers={[-1, -1]}
          selectedValue={stateValue}
          onChange={setStateValue}
          altColor={Colors.BLACK}
        />
      </div>
      <div
        className="AllowScroll"
        style={{ paddingBottom: 200, paddingTop: 7 }}
      >
        <FlatList
          list={itemsToShow}
          renderItem={(item: Event) => (
            <>
              <GenericActionItem
                leftComp={
                  <EventItemImage
                    item={item}
                    size={48}
                    containerStyles={{ borderRadius: 6 }}
                  />
                }
                textComp={
                  <div className="ColumnNormalSelect" style={{ gap: 4 }}>
                    <span className="OneLineText bodyMedium">{item.title}</span>
                    <span
                      className="smallBodySubtext OneLineText"
                      style={{ width: 340 }}
                    >
                      {selectType === SelectRecipientType.EVENTS
                        ? isEventRelatedType(item.eventType)
                          ? getEventDisplayDate(item)
                          : `Created: ${GetDateWithTime(item.createdAt, true)}`
                        : `${item.externalLink}`}
                    </span>
                  </div>
                }
                rightComp={
                  <Icon
                    icon="ion:chevron-right"
                    height={16}
                    style={{ color: Colors.GRAY1 }}
                  />
                }
                onPress={() => updateSelectType({ selectedItem: item })}
              />
              <HorizontalDivider altMargin={10} />
            </>
          )}
          renderWhenEmpty={() => (
            <EmptyStateFlatlist
              containerStyles={{ paddingTop: 200 }}
              isLoading={false}
              searchTerm={searchTerm}
              nonSearchEmptyView={
                <EmptyStateButton
                  title={"No Items"}
                  description={
                    "Your items in this product type will show up here"
                  }
                  icon={
                    <Icon
                      icon={
                        selectType === SelectRecipientType.EVENTS
                          ? selectGroupType.eventType &&
                            isEventRelatedType(selectGroupType.eventType)
                            ? "ion:calendar"
                            : "ion:bag-handle"
                          : "ion:document-text"
                      }
                      height={64}
                      style={{ color: Colors.GRAY1 }}
                    />
                  }
                  iconBox={84}
                  containerStyles={{ paddingTop: 100 }}
                />
              }
            />
          )}
        />
      </div>
    </>
  );
};

export default SelectRecipientsEventLibrary;
